import { AppointmentModel } from "@devexpress/dx-react-scheduler";
import { AppointmentTooltip as DXAppointmentTooltip } from "@devexpress/dx-react-scheduler-material-ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import * as React from "react";

import Lens from "@mui/icons-material/Lens";
import ReadMoreIcon from "@mui/icons-material/ReadMore";
import SyncIcon from "@mui/icons-material/Sync";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import classNames from "clsx";
import { format } from "date-fns";

import { useAppSelector } from "app/hooks";
import { misc, neutral } from "app/themes/colors";
import AppointmentStatus from "components/appointment-status";
import {
  APPOINTMENT_TYPES_CATEGORIES,
  APPOINTMENT_TYPE_GENERAL_TYPE_SCHEDULER
} from "constants/appointment-types";
import {
  APPOINTMENT_STATUS_DRAFT,
  RECURRENCE_DAYS,
  RECURRENCE_WEEKS,
  REOCCURRENCE_PERIODS,
  REOCCURRENCE_PERIOD_TYPE_MONTHLY,
  REOCCURRENCE_PERIOD_TYPE_WEEKLY
} from "constants/appointments";
import { NON_BREAKABLE_SPACE } from "constants/common";
import {
  GOOGLE_CALENDAR_SYNC_FEATURE,
  NEEDS_RESCHEDULE_FEATURE,
  RECURRING_APPOINTMENTS_FEATURE,
  ZOOM_TELEHEALTH_INTEGRATION
} from "constants/feature-flags";
import { selectCurrentUser } from "features/auth/auth-slice";
import { usePatientDrawer } from "features/patients/patient-drawer";
import { useZoomLinkGeneration } from "features/scheduling/hooks/use-zoom-link-generation.hook";
import { useSyncAppointmentToGoogleMutation } from "services/appointments";
import { useGetUserQuery } from "services/users";
import { formatBackendDateOnly } from "utils/common";

import { classes } from "./appointment-preview.component";

type AppointmentPreviewTitleProps = {
  appointmentData: DXAppointmentTooltip.ContentProps["appointmentData"] &
    AppointmentModel;
  formatDate: DXAppointmentTooltip.ContentProps["formatDate"];
};
export default function AppointmentPreviewTitle({
  appointmentData,
  formatDate
}: AppointmentPreviewTitleProps) {
  const flags = useFlags();
  const currentUser = useAppSelector(selectCurrentUser);
  const { data: user } = useGetUserQuery(currentUser.id, {
    skip: !currentUser.id || currentUser.id === -1,
    refetchOnMountOrArgChange: true
  });
  const needsRescheduleFeature = flags[NEEDS_RESCHEDULE_FEATURE];
  const recurringAppointmentFeatureEnabled =
    flags[RECURRING_APPOINTMENTS_FEATURE];

  const { handleOpen: openPatientDrawer, setIsCollapsed } = usePatientDrawer();

  const [syncAppointmentToGoogle, { isLoading }] =
    useSyncAppointmentToGoogleMutation();
  const zoomAppointmentId = appointmentData?.modelId
    ? Number(appointmentData.modelId)
    : appointmentData?.id
      ? Number(appointmentData.id)
      : undefined;
  const { handleGenerateZoomLink, isZoomGenerating } =
    useZoomLinkGeneration(zoomAppointmentId);

  if (!appointmentData?.appointmentType) return null;
  const {
    id,
    modelId,
    patient,
    webPatient,
    status,
    title,
    appointmentType: { name: appointmentTypeName, general_type },
    startDate,
    endDate,
    needs_reschedule,
    schedule,
    staffMemberId,
    zoom_join_url,
    appointmentType
  } = appointmentData;

  const { recurrence_day, recurrence_period, end_date, recurrence_week } =
    schedule ?? {};

  const isZoomTelehealthEnabled = flags[ZOOM_TELEHEALTH_INTEGRATION];
  const isVirtualAppointment =
    isZoomTelehealthEnabled && appointmentType?.is_virtual;

  const isGoogleCalendarIntegrationEnabled =
    flags[GOOGLE_CALENDAR_SYNC_FEATURE] &&
    user?.sync_with_google_calendar &&
    user?.id === staffMemberId;

  const isEveryDayInAWeekOfTheMonth =
    recurrence_period === REOCCURRENCE_PERIOD_TYPE_MONTHLY &&
    Boolean(recurrence_week);

  const webSchedulerLabel =
    general_type === Number(APPOINTMENT_TYPE_GENERAL_TYPE_SCHEDULER) &&
    status === APPOINTMENT_STATUS_DRAFT
      ? ` (${APPOINTMENT_TYPES_CATEGORIES.getFromCode(
          APPOINTMENT_TYPE_GENERAL_TYPE_SCHEDULER
        )})`
      : "";

  const appointmentPatient = patient ? patient : webPatient;

  return (
    <Grid container alignItems="flex-start" className={classes.titleContainer}>
      <Grid item xs={2} pt={0.75} className={classNames(classes.textCenter)}>
        <Lens className={classNames(classes.colorfulContent)} />
      </Grid>
      <Grid item xs={10}>
        <Stack>
          {patient?.id ? (
            <Link
              className={classNames(classes.title, classes.dateAndTitle)}
              data-testid="appointment-preview-title"
              href={`/patients/${patient?.id}/appointments/${modelId || id}`}
              sx={{
                color: neutral[900],
                textDecorationColor: neutral[600],
                "&:hover": {
                  color: misc.purpleText
                }
              }}
            >
              {appointmentTypeName} with&nbsp;
              {[
                appointmentPatient.first_name,
                appointmentPatient.preferred_name
                  ? `(${appointmentPatient.preferred_name})`
                  : "",
                appointmentPatient.last_name
              ].join(NON_BREAKABLE_SPACE)}
              {title ? `${NON_BREAKABLE_SPACE}(${title})` : ""}
              {webSchedulerLabel}
            </Link>
          ) : (
            <Typography
              className={classNames(classes.title, classes.dateAndTitle)}
            >
              {appointmentTypeName}
              {webSchedulerLabel} with{" "}
              {[
                appointmentPatient.first_name,
                appointmentPatient.preferred_name
                  ? `(${appointmentPatient.preferred_name})`
                  : "",
                appointmentPatient.last_name
              ].join(" ")}
              {title ? ` (${title})` : ""}
            </Typography>
          )}
          <Typography
            className={classNames(classes.text, classes.dateAndTitle)}
          >
            {format(new Date(startDate), "EEEE, LLLL do")} /{" "}
            {`${formatDate(startDate, {
              hour: "numeric",
              minute: "numeric"
            })}
              - ${formatDate(endDate, {
                hour: "numeric",
                minute: "numeric"
              })}`}
          </Typography>

          {needs_reschedule && needsRescheduleFeature && (
            <Typography sx={{ color: misc.warningText, mt: 1, mb: -1 }}>
              Needs to be Rescheduled
            </Typography>
          )}
          <Box display="flex" gap={1} alignItems="flex-end" mt={1}>
            {(status || status === 0) && (
              <AppointmentStatus
                statusCode={status}
                needsReschedule={needs_reschedule}
              />
            )}

            {patient?.id && (
              <Button
                size="medium"
                endIcon={<ReadMoreIcon />}
                sx={{
                  fontWeight: 700,
                  textTransform: "none",
                  height: "32px",
                  padding: "8px 12px !important"
                }}
                onClick={() => {
                  openPatientDrawer(patient.id);
                  setIsCollapsed(false);
                }}
              >
                QuickView Patient
              </Button>
            )}

            {isGoogleCalendarIntegrationEnabled && (
              <Tooltip title="Sync Appointment to Google Calendar">
                <IconButton
                  disabled={isLoading}
                  onClick={() =>
                    (modelId || id) &&
                    syncAppointmentToGoogle(Number(modelId || id))
                  }
                  size="small"
                >
                  <SyncIcon />
                </IconButton>
              </Tooltip>
            )}
            {isVirtualAppointment &&
              (zoom_join_url ? (
                <Tooltip title="Join Zoom Meeting">
                  <IconButton
                    size="small"
                    onClick={() => window.open(zoom_join_url, "_blank")}
                  >
                    <VideocamIcon />
                  </IconButton>
                </Tooltip>
              ) : isZoomGenerating ? (
                <Tooltip title="Generating Zoom link...">
                  <IconButton size="small" disabled>
                    <CircularProgress size={20} />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Generate Zoom Link">
                  <IconButton size="small" onClick={handleGenerateZoomLink}>
                    <VideocamOutlinedIcon color="action" />
                  </IconButton>
                </Tooltip>
              ))}
          </Box>

          {recurringAppointmentFeatureEnabled && (
            <>
              {recurrence_period && !isEveryDayInAWeekOfTheMonth && (
                <Typography mt={2}>
                  {REOCCURRENCE_PERIODS.getFromCode(recurrence_period)} on{" "}
                  {recurrence_period === REOCCURRENCE_PERIOD_TYPE_WEEKLY
                    ? RECURRENCE_DAYS.getFromCode(recurrence_day)
                    : `the ${format(new Date(startDate), "do")}`}
                  {Boolean(end_date) && end_date !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(String(end_date), "EEE MMM dd")}
                    </>
                  )}
                </Typography>
              )}
              {recurrence_period && isEveryDayInAWeekOfTheMonth && (
                <Typography mt={2}>
                  {`${RECURRENCE_DAYS.getFromCode(
                    recurrence_day
                  )}, ${RECURRENCE_WEEKS.getFromCode(recurrence_week)}`}
                  {Boolean(end_date) && end_date !== null && (
                    <>
                      {" "}
                      until{" "}
                      {formatBackendDateOnly(String(end_date), "EEE MMM dd")}
                    </>
                  )}
                </Typography>
              )}
            </>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
}
